import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import { generateAccessCode, generateUUID } from '@/lib/validation';
import { useAuthReady } from '@/hooks/useAuthReady';
import { reportBookingError } from '@/lib/errorMonitoring';
import type { Booking, CreateBookingInput, BookingStatus } from '@/types/database';
import { dbBillingCode } from '@/lib/billingErrors';

// Query keys
export const bookingKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingKeys.all, 'list'] as const,
  list: (landlordId: string) => [...bookingKeys.lists(), landlordId] as const,
  listByUnit: (unitId: string) => [...bookingKeys.lists(), 'unit', unitId] as const,
  paginated: (landlordId: string, page: number, pageSize: number) =>
    [...bookingKeys.lists(), landlordId, 'page', page, pageSize] as const,
  details: () => [...bookingKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingKeys.details(), id] as const,
};

// Extended booking with unit and property info
export interface BookingWithDetails extends Booking {
  unit?: {
    id: string;
    name: string;
    property_id: string;
    base_price: number;
    cleaning_fee: number;
    property?: {
      id: string;
      name: string;
      city: string;
    };
  };
}

// Fetch all bookings for current user
export function useBookings() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: bookingKeys.list(userId ?? ''),
    queryFn: async (): Promise<BookingWithDetails[]> => {
      if (!userId) return [];

      const { data, error } = await getSupabase()
        .from('bookings')
        .select(`
          *,
          unit:units (
            id,
            name,
            property_id,
            base_price,
            cleaning_fee,
            property:properties (
              id,
              name,
              city
            )
          )
        `)
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        // Newest first so the 1000-row cap drops the oldest history, never the
        // upcoming stays (the list re-sorts for display).
        .order('check_in', { ascending: false })
        .limit(1000);

      if (error) throw error;
      return data ?? [];
    },
    enabled: isReady,
  });
}

// Fetch bookings with server-side pagination
export function usePaginatedBookings(page: number = 1, pageSize: number = 20) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: bookingKeys.paginated(userId ?? '', page, pageSize),
    queryFn: async () => {
      if (!userId) return { data: [] as BookingWithDetails[], count: 0 };

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await getSupabase()
        .from('bookings')
        .select(`
          *,
          unit:units (
            id,
            name,
            property_id,
            base_price,
            cleaning_fee,
            property:properties (
              id,
              name,
              city
            )
          )
        `, { count: 'exact' })
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .order('check_in', { ascending: true })
        .range(from, to);

      if (error) throw error;
      return { data: (data ?? []) as BookingWithDetails[], count: count ?? 0 };
    },
    enabled: isReady,
    placeholderData: (prev) => prev,
  });
}

// Fetch bookings by unit (for calendar)
export function useBookingsByUnit(unitId: string | undefined) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: bookingKeys.listByUnit(unitId ?? ''),
    queryFn: async (): Promise<Booking[]> => {
      if (!unitId || !userId) return [];

      const { data, error } = await getSupabase()
        .from('bookings')
        .select('*')
        .eq('unit_id', unitId)
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .order('check_in', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!unitId && isReady,
  });
}

// Fetch single booking by ID
export function useBooking(id: string | undefined) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: bookingKeys.detail(id ?? ''),
    queryFn: async (): Promise<BookingWithDetails | null> => {
      if (!id || !userId) return null;

      const { data, error } = await getSupabase()
        .from('bookings')
        .select(`
          *,
          unit:units (
            id,
            name,
            property_id,
            base_price,
            cleaning_fee,
            property:properties (
              id,
              name,
              city
            )
          )
        `)
        .eq('id', id)
        .eq('landlord_id', userId)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return data;
    },
    enabled: !!id && isReady,
  });
}

// Create booking mutation
export function useCreateBooking() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (input: CreateBookingInput): Promise<Booking> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Generate access code and token
      const accessCode = generateAccessCode();
      const accessToken = generateUUID();

      const { data, error } = await getSupabase()
        .from('bookings')
        .insert({
          ...input,
          landlord_id: user.id,
          // The form's status choice wins (it defaults to "confirmed"); before,
          // a hard-coded 'pending' overrode it, so every new booking was
          // missing from today's arrivals, reminders and revenue.
          status: input.status ?? ('pending' as BookingStatus),
          // The cancel-metadata trigger only runs on UPDATE.
          ...(input.status === 'cancelled'
            ? { cancelled_at: new Date().toISOString(), cancelled_by: 'landlord' }
            : {}),
          access_code: accessCode,
          access_token: accessToken,
        })
        .select()
        .single();

      if (error) {
        // Postgres exclusion_violation = our no_overlapping_bookings constraint
        if (error.code === '23P01' || error.message?.includes('no_overlapping_bookings')) {
          const friendly = new Error('DOUBLE_BOOKING') as Error & { code?: string };
          friendly.code = 'DOUBLE_BOOKING';
          throw friendly;
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.listByUnit(data.unit_id) });
      // Calendars, availability checks and the occupancy card read bookings too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
    },
    onError: (error, input) => {
      // DOUBLE_BOOKING is expected (overlap) — don't ship to Sentry as a bug.
      if ((error as Error & { code?: string })?.code === 'DOUBLE_BOOKING') return;
      // Plan ended / unit deactivated: an expected refusal, shown to the host.
      const billingCode = dbBillingCode(error);
      if (billingCode === 'PLAN_REQUIRED' || billingCode === 'UNIT_INACTIVE') return;
      reportBookingError(error, {
        action: 'create',
        landlordId: user?.id,
        unitId: input?.unit_id,
      });
    },
  });
}

// Update booking mutation
export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Booking> & { id: string }): Promise<Booking> => {
      const { data, error } = await getSupabase()
        .from('bookings')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (error.code === '23P01' || error.message?.includes('no_overlapping_bookings')) {
          const friendly = new Error('DOUBLE_BOOKING') as Error & { code?: string };
          friendly.code = 'DOUBLE_BOOKING';
          throw friendly;
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.listByUnit(data.unit_id) });
      // Calendars, availability checks and the occupancy card read bookings too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
    },
    onError: (error, vars) => {
      if ((error as Error & { code?: string })?.code === 'DOUBLE_BOOKING') return;
      reportBookingError(error, { action: 'update', bookingId: vars?.id });
    },
  });
}

// Update booking status mutation
export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      cancellation_reason,
      cancellation_message,
    }: {
      id: string;
      status: BookingStatus;
      cancellation_reason?: string | null;
      cancellation_message?: string | null;
    }): Promise<Booking> => {
      const updates: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'cancelled') {
        // cancelled_at and cancelled_by are auto-filled by DB trigger
        if (cancellation_reason !== undefined) updates.cancellation_reason = cancellation_reason;
        if (cancellation_message !== undefined) updates.cancellation_message = cancellation_message;
      }

      const { data, error } = await getSupabase()
        .from('bookings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        // Un-cancelling a booking whose dates were rebooked meanwhile.
        if (error.code === '23P01' || error.message?.includes('no_overlapping_bookings')) {
          const friendly = new Error('DOUBLE_BOOKING') as Error & { code?: string };
          friendly.code = 'DOUBLE_BOOKING';
          throw friendly;
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.listByUnit(data.unit_id) });
      // Calendars, availability checks and the occupancy card read bookings too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
    },
    onError: (error, vars) => {
      if ((error as Error & { code?: string })?.code === 'DOUBLE_BOOKING') return;
      reportBookingError(error, {
        action: 'update_status',
        bookingId: vars?.id,
      });
    },
  });
}

// Soft delete booking mutation (moves to trash for 30 days)
// Cascades soft delete to: payments, booking_documents
export interface DeleteBookingResult {
  ok: boolean;
  error_code?: 'FORBIDDEN' | 'NOT_FOUND';
  error_message?: string;
  cascade_payments?: number;
}

export function useDeleteBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ unitId?: string; result: DeleteBookingResult }> => {
      // First get the booking to know unit_id for cache invalidation
      const { data: booking } = await getSupabase()
        .from('bookings')
        .select('unit_id')
        .eq('id', id)
        .single();

      const unitId = booking?.unit_id;

      const { data, error } = await getSupabase().rpc('soft_delete_booking', {
        p_booking_id: id,
      });
      if (error) throw error;

      const result = data as DeleteBookingResult;
      if (!result.ok) {
        const err = new Error(result.error_message || result.error_code || 'Delete failed') as Error & {
          code?: string;
          result?: DeleteBookingResult;
        };
        err.code = result.error_code;
        err.result = result;
        throw err;
      }
      return { unitId, result };
    },
    onSuccess: ({ unitId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['booking-documents'] });
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      // Calendars, availability checks and the occupancy card read bookings too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
      if (unitId) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.listByUnit(unitId) });
      }
    },
    onError: (error, bookingId) => {
      reportBookingError(error, { action: 'delete', bookingId });
    },
  });
}

// Utility function to calculate total price
export function calculateBookingPrice(
  checkIn: string,
  checkOut: string,
  basePrice: number,
  cleaningFee: number = 0
): { nights: number; totalPrice: number } {
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const nights = Math.ceil(
    (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const totalPrice = nights * basePrice + cleaningFee;
  return { nights, totalPrice };
}

// Utility function to format date range
export function formatDateRange(checkIn: string, checkOut: string, locale = 'hr-HR'): string {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
  };
  // "2026-10-21" is a calendar date — parse it as local midnight, not UTC
  // (UTC midnight shows the previous day west of Greenwich).
  const toDate = (v: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  };
  return `${toDate(checkIn).toLocaleDateString(locale, options)} - ${toDate(checkOut).toLocaleDateString(locale, options)}`;
}

// Get booking status color
export function getStatusColor(status: BookingStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'confirmed':
      return 'bg-blue-100 text-blue-800';
    case 'checked_in':
      return 'bg-green-100 text-green-800';
    case 'completed':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Check if dates are available for a unit
export interface DateConflict {
  hasConflict: boolean;
  conflictingBooking?: {
    id: string;
    guest_name: string;
    check_in: string;
    check_out: string;
  };
}

export function useCheckAvailability() {
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({
      unitId,
      checkIn,
      checkOut,
      excludeBookingId,
    }: {
      unitId: string;
      checkIn: string;
      checkOut: string;
      excludeBookingId?: string;
    }): Promise<DateConflict> => {
      if (!user?.id || !unitId || !checkIn || !checkOut) {
        return { hasConflict: false };
      }

      // Find overlapping bookings
      // A booking overlaps if: existing.check_in < new.check_out AND existing.check_out > new.check_in
      let query = getSupabase()
        .from('bookings')
        .select('id, guest_name, check_in, check_out')
        .eq('unit_id', unitId)
        .neq('status', 'cancelled')
        .is('deleted_at', null)
        .lt('check_in', checkOut)
        .gt('check_out', checkIn);

      // Exclude current booking when editing
      if (excludeBookingId) {
        query = query.neq('id', excludeBookingId);
      }

      const { data, error } = await query.limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        return {
          hasConflict: true,
          conflictingBooking: data[0],
        };
      }

      return { hasConflict: false };
    },
  });
}
